import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
} from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Ng2ImgMaxService } from "ng2-img-max";
import { BehaviorSubject, take } from "rxjs";
import { Option } from "src/app/core/contracts/models/option";
import { DynamicSelectService } from "src/app/core/services/api/dynamic-select.service";
import { IndustriasService } from "src/app/core/services/api/industrias.service";
import { UbigeoService } from "src/app/core/services/api/ubigeo.service";
import { Swal2 } from "src/app/core/utils/swal2.util";

@Component({
    selector: "app-form-configuration",
    templateUrl: "./form-configuration.component.html",
    styleUrl: "./form-configuration.component.scss",
})
export class FormConfigurationComponent implements OnInit {
    @Input() tenant: any = {};
    @Input() mode: "step" | "configuration" = "configuration";

    @Output() onFinalize = new EventEmitter<any>();
    @Output() onVolver = new EventEmitter<void>();
    @Output() onSave = new EventEmitter<any>();

    regiones: any[] = [];
    countries: any[] = [];

    industrial: any[] = [];

    temporaryImageLogo: any;
    temporaryImageShield: any;

    customer: any;

    resizing = new BehaviorSubject<boolean>(false);
    resizing$ = this.resizing.asObservable();

    private changeDetectorRef = inject(ChangeDetectorRef);

    /* documents: Option[] = []; */
    documentTypes = [
        { code: "ruc", name: "RUC", id: 1 },
        { code: "dni", name: "DNI", id: 2 },
        { code: "ce", name: "Carnet de extranjería", id: 3 },
        { code: "pas", name: "Pasaporte", id: 4 },
    ];

    step4Form: FormGroup = this.formBuilder.group({
        country: ["", Validators.required],
        filterCountry: [null],
        region: ["", Validators.required],
        filterRegion: [null],
        alias: ["", [Validators.required, Validators.maxLength(16)]],
        documentType: ["", Validators.required],
        documentNumber: ["", [Validators.required, Validators.minLength(3)]],
        industrialSector: ["", Validators.required],
        filterIdustrialSector: [null],
        companyName: ["", Validators.required],
        address: ["", Validators.required],
        additionalConfig: [false, Validators.requiredTrue],
        fileLogo: [""],
        fileShield: [""],
    });

    serverLoading: boolean = false;
    registerLoading: boolean = false;
    isReadOnly: boolean = false;

    constructor(
        private formBuilder: FormBuilder,
        private ubigeoService: UbigeoService,
        private ng2ImgMax: Ng2ImgMaxService,
        private swal: Swal2,
        private industriasService: IndustriasService,
        /*  private dynamicSelectService: DynamicSelectService, */
    ) { }

    ngOnInit(): void {

        if (this.tenant && Object.keys(this.tenant).length > 0) {
            this.isReadOnly = true;
            this.setTenantData();
        } else {
            this.isReadOnly = false;
        }

        this.ubigeoService
            .getCountries()
            .subscribe((res) => (this.countries = res.countries));

        this.step4Form.get("country")?.valueChanges.subscribe((paisId) => {
            this.ubigeoService.getRegions(paisId).subscribe((res) => {
                this.regiones = res.regions;
                this.step4Form.get("region")?.setValue(null);
            });
        });

        this.getAll();
    }


    /** Se arrastró un archivo que no es PNG ni JPG */
    onPhotoRejected() {
        this.swal.showAlert("Por favor, elija una imagen en formato JPG o PNG", "info");
    }

    addNewPhoto(image: File[] | FileList, type: "logo" | "shield") {
        if (image.length === 0) return;

        const reader = new FileReader();
        this.resizing.next(true);

        this.ng2ImgMax
            .compressImage(image[0], 2)
            .pipe(take(1))
            .subscribe(
                (result) => {
                    reader.readAsDataURL(result);
                    reader.onload = () => {
                        const base64 = reader.result as string;

                        if (type === "logo") {
                            this.temporaryImageLogo = base64;
                            this.step4Form.get("fileLogo")?.setValue(base64);
                        } else {
                            this.temporaryImageShield = base64;
                            this.step4Form.get("fileShield")?.setValue(base64);
                        }

                        this.step4Form.markAsDirty();

                        this.resizing.next(false);
                        this.changeDetectorRef.detectChanges();
                    };
                },
                (error) => {
                    console.warn(error);
                    this.resizing.next(false);
                    this.swal.showAlert(
                        "Por favor, elija una imagen en formato JPG o PNG",
                        "info"
                    );

                    if (type === "logo") {
                        this.temporaryImageLogo = null;
                    } else {
                        this.temporaryImageShield = null;
                    }
                    this.changeDetectorRef.detectChanges();
                }
            );
    }


    finalizar() {
        this.registerLoading = true
        if (this.step4Form.valid) {
            this.onFinalize.emit(this.step4Form.value);
        } else {
            this.step4Form.markAllAsTouched();
        }
    }

    onSaveForm() {
        this.serverLoading = true;
        if (this.step4Form.valid) {
            this.onSave.emit(this.step4Form.value);
        } else {
            this.step4Form.markAllAsTouched();
        }
    }

    getAll(): void {
        this.industriasService.getAll().subscribe({
            next: (response: any) => {
                this.industrial = response.industrias;
            },
            error: (error: any) => {
                console.error("Error al cargar industrias:", error);
            },
        });
    }

    setTenantData() {
        if (this.tenant.pais_id) {
            this.ubigeoService.getRegions(this.tenant.pais_id)
                .subscribe((res) => {
                    this.regiones = res.regions;
                    this.step4Form.patchValue({
                        region: this.tenant.region_id,
                    });
                });

        }
        this.step4Form.patchValue({
            country: this.tenant.pais_id,
            alias: this.tenant.slug,
            documentType: this.tenant.tipo_documento,
            documentNumber: this.tenant.nro_documento,
            industrialSector: this.tenant.industria_id,
            companyName: this.tenant.name,
            address: this.tenant.direccion,
            additionalConfig: this.tenant.valorizacion_alcance === 1,
        });

        //Img xd
        this.temporaryImageLogo = null;
        this.temporaryImageShield = null;
        this.step4Form.markAsPristine();
    }

    toLowercaseAlias() {
        const value = this.step4Form.get('alias')?.value;
        if (value) {
            this.step4Form.get('alias')?.setValue(value.toLowerCase(), { emitEvent: false });
        }
    }
}
